/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { TableCellNode, TableDOMCell, TableMapType } from '@lexical/table';
import {
  $computeTableMapSkipCellCheck,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $isTableCellNode,
  $isTableRowNode,
  getDOMCellFromTarget,
  getTableElement,
  TableNode,
} from '@lexical/table';
import type { LexicalEditor, NodeKey } from 'lexical';
import {
  $getNearestNodeFromDOMNode,
  isHTMLElement,
  SKIP_SCROLL_INTO_VIEW_TAG,
} from 'lexical';
import type { JSX } from 'react';
import {
  CSSProperties,
  PointerEventHandler,
  ReactPortal,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import './index.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { calculateZoomLevel, mergeRegister } from '@lexical/utils';
import { createPortal } from 'react-dom';

type PointerPosition = {
  x: number;
  y: number;
};

type PointerDraggingDirection = 'right' | 'bottom';

const MIN_ROW_HEIGHT = 33;
const MIN_COLUMN_WIDTH = 92;

function TableCellResizer({ editor }: { editor: LexicalEditor }): JSX.Element {
  const targetRef = useRef<HTMLElement | null>(null);
  const resizerRef = useRef<HTMLDivElement | null>(null);
  const tableRectRef = useRef<ClientRect | null>(null);
  const [hasTable, setHasTable] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);

  const pointerStartPosRef = useRef<PointerPosition | null>(null);
  const [pointerCurrentPos, updatePointerCurrentPos] =
    useState<PointerPosition | null>(null);

  const [activeCell, updateActiveCell] = useState<TableDOMCell | null>(null);
  const [draggingDirection, updateDraggingDirection] =
    useState<PointerDraggingDirection | null>(null);

  const resetState = useCallback(() => {
    updateActiveCell(null);
    targetRef.current = null;
    updateDraggingDirection(null);
    pointerStartPosRef.current = null;
    tableRectRef.current = null;
  }, []);

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (rootElement) {
      const editorContainer = rootElement.closest(
        '.lexical-editor-container',
      ) as HTMLElement;
      containerRef.current = editorContainer;
    }
  }, [editor]);

  useEffect(() => {
    const tableKeys = new Set<NodeKey>();
    return mergeRegister(
      editor.registerMutationListener(TableNode, (nodeMutations) => {
        for (const [nodeKey, mutation] of nodeMutations) {
          if (mutation === 'destroyed') {
            tableKeys.delete(nodeKey);
          } else {
            tableKeys.add(nodeKey);
          }
        }
        setHasTable(tableKeys.size > 0);
      }),
    );
  }, [editor]);

  useEffect(() => {
    if (!hasTable) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const target = event.target;
      if (!isHTMLElement(target)) {
        return;
      }

      if (draggingDirection) {
        event.preventDefault();
        event.stopPropagation();
        updatePointerCurrentPos({
          x: event.clientX,
          y: event.clientY,
        });
        return;
      }
      if (resizerRef.current && resizerRef.current.contains(target)) {
        return;
      }

      if (targetRef.current !== target) {
        targetRef.current = target;
        const cell = getDOMCellFromTarget(target);

        if (cell && activeCell !== cell) {
          editor.getEditorState().read(
            () => {
              const tableCellNode = $getNearestNodeFromDOMNode(cell.elem);
              if (!tableCellNode) {
                throw new Error('TableCellResizer: Table cell node not found.');
              }

              const tableNode =
                $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
              const tableElement = getTableElement(
                tableNode,
                editor.getElementByKey(tableNode.getKey()),
              );

              if (!tableElement) {
                throw new Error('TableCellResizer: Table element not found.');
              }

              targetRef.current = target;
              tableRectRef.current = tableElement.getBoundingClientRect();
              updateActiveCell(cell);
            },
            { editor },
          );
        } else if (cell == null) {
          resetState();
        }
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      const isTouchEvent = event.pointerType === 'touch';
      if (isTouchEvent) {
        onPointerMove(event);
      }
    };

    const resizerContainer = resizerRef.current;
    resizerContainer?.addEventListener('pointermove', onPointerMove, {
      capture: true,
    });

    const removeRootListener = editor.registerRootListener(
      (rootElement, prevRootElement) => {
        prevRootElement?.removeEventListener('pointermove', onPointerMove);
        prevRootElement?.removeEventListener('pointerdown', onPointerDown);
        rootElement?.addEventListener('pointermove', onPointerMove);
        rootElement?.addEventListener('pointerdown', onPointerDown);
      },
    );

    return () => {
      removeRootListener();
      resizerContainer?.removeEventListener('pointermove', onPointerMove);
    };
  }, [activeCell, draggingDirection, editor, resetState, hasTable]);

  const isHeightChanging = (direction: PointerDraggingDirection) => {
    if (direction === 'bottom') {
      return true;
    }
    return false;
  };

  const getCellNodeHeight = (
    cell: TableCellNode,
    activeEditor: LexicalEditor,
  ): number | undefined => {
    const domCellNode = activeEditor.getElementByKey(cell.getKey());
    return domCellNode?.clientHeight;
  };

  const updateRowHeight = useCallback(
    (heightChange: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }

      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
          if (!$isTableCellNode(tableCellNode)) {
            throw new Error('TableCellResizer: Table cell node not found.');
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const baseRowIndex =
            $getTableRowIndexFromTableCellNode(tableCellNode);
          const tableRows = tableNode.getChildren();

          // Determine if this is a full row merge by checking colspan
          const isFullRowMerge =
            tableCellNode.getColSpan() === tableNode.getColumnCount();

          // For full row merges, apply to first row. For partial merges, apply to last row
          const tableRowIndex = isFullRowMerge
            ? baseRowIndex
            : baseRowIndex + tableCellNode.getRowSpan() - 1;

          if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
            throw new Error('Expected table cell to be inside of table row.');
          }

          const tableRow = tableRows[tableRowIndex];

          if (!$isTableRowNode(tableRow)) {
            throw new Error('Expected table row');
          }

          let height = tableRow.getHeight();
          if (height === undefined) {
            const rowCells = tableRow.getChildren<TableCellNode>();
            height = Math.min(
              ...rowCells.map(
                (cell) => getCellNodeHeight(cell, editor) ?? Infinity,
              ),
            );
          }

          const newHeight = Math.max(height + heightChange, MIN_ROW_HEIGHT);
          tableRow.setHeight(newHeight);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
    },
    [activeCell, editor],
  );

  const getCellColumnIndex = (
    tableCellNode: TableCellNode,
    tableMap: TableMapType,
  ) => {
    for (let row = 0; row < tableMap.length; row++) {
      for (let column = 0; column < tableMap[row].length; column++) {
        if (tableMap[row][column].cell === tableCellNode) {
          return column;
        }
      }
    }
    return undefined;
  };

  const updateColumnWidth = useCallback(
    (widthChange: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }
      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
          if (!$isTableCellNode(tableCellNode)) {
            throw new Error('TableCellResizer: Table cell node not found.');
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const [tableMap] = $computeTableMapSkipCellCheck(
            tableNode,
            null,
            null,
          );
          const columnIndex = getCellColumnIndex(tableCellNode, tableMap);
          if (columnIndex === undefined) {
            throw new Error('TableCellResizer: Table column not found.');
          }

          let colWidths = tableNode.getColWidths();

          // Lazy initialization: capture current widths on first resize
          if (!colWidths) {
            const tableElement = editor.getElementByKey(tableNode.getKey());
            if (tableElement) {
              const firstRow = tableElement.querySelector('tr');
              if (firstRow) {
                const cells = firstRow.querySelectorAll('th, td');
                colWidths = Array.from(cells).map(
                  (cell) =>
                    (cell as HTMLElement).offsetWidth || MIN_COLUMN_WIDTH,
                );
                tableNode.setColWidths(colWidths);
              }
            }
          }

          if (!colWidths) {
            return;
          }

          const width = colWidths[columnIndex];
          if (width === undefined) {
            return;
          }
          const newColWidths = [...colWidths];
          newColWidths[columnIndex] = Math.max(
            width + widthChange,
            MIN_COLUMN_WIDTH,
          );
          tableNode.setColWidths(newColWidths);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
    },
    [activeCell, editor],
  );

  const pointerUpHandler = useCallback(
    (direction: PointerDraggingDirection) => {
      const handler = (event: PointerEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (!activeCell) {
          throw new Error('TableCellResizer: Expected active cell.');
        }

        if (pointerStartPosRef.current) {
          const { x, y } = pointerStartPosRef.current;

          if (activeCell === null) {
            return;
          }
          const zoom = calculateZoomLevel(event.target as Element);

          if (isHeightChanging(direction)) {
            const heightChange = (event.clientY - y) / zoom;
            updateRowHeight(heightChange);
          } else {
            const widthChange = (event.clientX - x) / zoom;
            updateColumnWidth(widthChange);
          }

          resetState();
          document.removeEventListener('pointerup', handler);
        }
      };
      return handler;
    },
    [activeCell, resetState, updateColumnWidth, updateRowHeight],
  );

  const toggleResize = useCallback(
    (
      direction: PointerDraggingDirection,
    ): PointerEventHandler<HTMLDivElement> =>
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!activeCell) {
          throw new Error('TableCellResizer: Expected active cell.');
        }

        pointerStartPosRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
        updatePointerCurrentPos(pointerStartPosRef.current);
        updateDraggingDirection(direction);

        document.addEventListener('pointerup', pointerUpHandler(direction));
      },
    [activeCell, pointerUpHandler],
  );

  const [resizerStyles, setResizerStyles] = useState<
    Record<string, CSSProperties | null>
  >({
    bottom: null,
    left: null,
    right: null,
    top: null,
  });

  useEffect(() => {
    if (!activeCell || !containerRef.current) {
      setResizerStyles({
        bottom: null,
        left: null,
        right: null,
        top: null,
      });
      return;
    }

    const cellRect = activeCell.elem.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const zoom = calculateZoomLevel(activeCell.elem);
    const zoneWidth = 16;

    const relativeLeft = cellRect.left - containerRect.left;
    const relativeTop = cellRect.top - containerRect.top;

    const styles: Record<string, CSSProperties> = {
      bottom: {
        backgroundColor: 'none',
        cursor: 'row-resize',
        height: `${zoneWidth}px`,
        left: `${relativeLeft}px`,
        top: `${relativeTop + cellRect.height - zoneWidth / 2}px`,
        width: `${cellRect.width}px`,
        position: 'absolute',
      },
      right: {
        backgroundColor: 'none',
        cursor: 'col-resize',
        height: `${cellRect.height}px`,
        left: `${relativeLeft + cellRect.width - zoneWidth / 2}px`,
        top: `${relativeTop}px`,
        width: `${zoneWidth}px`,
        position: 'absolute',
      },
    };

    const tableRect = tableRectRef.current;

    if (draggingDirection && pointerCurrentPos && tableRect && containerRect) {
      const relativeTableLeft = tableRect.left - containerRect.left;
      const relativeTableTop = tableRect.top - containerRect.top;

      if (isHeightChanging(draggingDirection)) {
        styles[draggingDirection].left = `${relativeTableLeft}px`;
        styles[draggingDirection].top =
          `${(pointerCurrentPos.y - containerRect.top) / zoom}px`;
        styles[draggingDirection].height = '3px';
        styles[draggingDirection].width = `${tableRect.width}px`;
      } else {
        styles[draggingDirection].top = `${relativeTableTop}px`;
        styles[draggingDirection].left =
          `${(pointerCurrentPos.x - containerRect.left) / zoom}px`;
        styles[draggingDirection].width = '3px';
        styles[draggingDirection].height = `${tableRect.height}px`;
      }

      styles[draggingDirection].backgroundColor = '#adf';
      styles[draggingDirection].mixBlendMode = 'unset';
    }

    setResizerStyles(styles);
  }, [activeCell, draggingDirection, pointerCurrentPos]);

  return (
    <div ref={resizerRef}>
      {activeCell != null && (
        <>
          <div
            className="TableCellResizer__resizer TableCellResizer__ui"
            style={resizerStyles.right || undefined}
            onPointerDown={toggleResize('right')}
          />
          <div
            className="TableCellResizer__resizer TableCellResizer__ui"
            style={resizerStyles.bottom || undefined}
            onPointerDown={toggleResize('bottom')}
          />
        </>
      )}
    </div>
  );
}

export function TableCellResizerPlugin(): null | ReactPortal {
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (rootElement) {
      const editorContainer = rootElement.closest(
        '.lexical-editor-container',
      ) as HTMLElement;
      setPortalTarget(editorContainer || document.body);
    }
  }, [editor]);

  return useMemo(
    () =>
      isEditable && portalTarget
        ? createPortal(<TableCellResizer editor={editor} />, portalTarget)
        : null,
    [editor, isEditable, portalTarget],
  );
}
